import { banco } from '../db/banco'
import { novoRegistroBase } from '../db/tipos'
import type { Cliente } from '../db/tipos'
import { gravarLocalEEnfileirar } from '../sync/outbox'
import { converterNumeroBR } from '../lib/textoBR'
import { encontrarDuplicata } from './dedupe'
import { normalizarRegime } from './mapeamento'
import type { LinhaMapeada } from './validacao'

export interface PadroesImportacao {
  regiao?: string
  segmento?: string
  potencial?: 'A' | 'B' | 'C' | 'D'
}

export interface ResultadoClientes {
  criados: number
  atualizados: number
  paraRevisar: Array<{ linha: LinhaMapeada; existente: Cliente }>
}

// Constroi um Cliente novo a partir da linha (padroes preenchem o que faltar)
export function clienteDaLinha(campos: Record<string, string>, padroes: PadroesImportacao): Cliente {
  return {
    ...novoRegistroBase(),
    razao_social: campos.razao_social,
    nome_fantasia: campos.nome_fantasia || null,
    cnpj_cpf: campos.cnpj_cpf || null,
    contatos:
      campos.telefone || campos.email || campos.ddd
        ? [{ ddd: campos.ddd || undefined, telefone: campos.telefone || undefined, email: campos.email || undefined }]
        : [],
    endereco: campos.endereco || null,
    bairro: campos.bairro || null,
    municipio: campos.municipio || null,
    uf: campos.uf || null,
    cep: campos.cep || null,
    regiao: campos.regiao || padroes.regiao || null,
    segmento: campos.segmento || padroes.segmento || null,
    inscricao_estadual: campos.inscricao_estadual || null,
    regime_tributario: campos.regime_tributario ? normalizarRegime(campos.regime_tributario) : null,
    linha_credito: campos.linha_credito ? converterNumeroBR(campos.linha_credito) : null,
    potencial: padroes.potencial ?? null,
    dia_visita: null,
    dia_entrega: null,
    status: 'ativo',
    motivo_inativacao: null,
    obs_inativacao: null,
    inativado_em: null,
    observacoes: null,
  }
}

// Preenche APENAS campos vazios do existente com os da linha (nunca sobrescreve)
export function mesclarPreenchendoVazios(existente: Cliente, campos: Record<string, string>): Cliente {
  const contatos = [...existente.contatos]
  if ((campos.telefone || campos.email || campos.ddd) && contatos.length === 0) {
    contatos.push({ ddd: campos.ddd || undefined, telefone: campos.telefone || undefined, email: campos.email || undefined })
  } else if (contatos.length > 0 && campos.ddd && !contatos[0].ddd) {
    contatos[0] = { ...contatos[0], ddd: campos.ddd }
  }
  return {
    ...existente,
    nome_fantasia: existente.nome_fantasia ?? (campos.nome_fantasia || null),
    cnpj_cpf: existente.cnpj_cpf ?? (campos.cnpj_cpf || null),
    contatos,
    endereco: existente.endereco ?? (campos.endereco || null),
    bairro: existente.bairro ?? (campos.bairro || null),
    municipio: existente.municipio ?? (campos.municipio || null),
    uf: existente.uf ?? (campos.uf || null),
    cep: existente.cep ?? (campos.cep || null),
    regiao: existente.regiao ?? (campos.regiao || null),
    segmento: existente.segmento ?? (campos.segmento || null),
    inscricao_estadual: existente.inscricao_estadual ?? (campos.inscricao_estadual || null),
    regime_tributario: existente.regime_tributario ?? (campos.regime_tributario ? normalizarRegime(campos.regime_tributario) : null),
    linha_credito: existente.linha_credito ?? (campos.linha_credito ? converterNumeroBR(campos.linha_credito) : null),
  }
}

export async function garantirVinculo(
  clienteId: string,
  representadaId: string,
  codigoNaRepresentada: string | null,
): Promise<void> {
  const existente = await banco.clientes_representadas
    .where('[cliente_id+representada_id]')
    .equals([clienteId, representadaId])
    .filter((v) => v.apagado_em === null)
    .first()
  if (existente) {
    if (codigoNaRepresentada && !existente.codigo_cliente_na_representada) {
      await gravarLocalEEnfileirar('clientes_representadas', {
        ...existente,
        codigo_cliente_na_representada: codigoNaRepresentada,
      })
    }
    return
  }
  await gravarLocalEEnfileirar('clientes_representadas', {
    ...novoRegistroBase(),
    cliente_id: clienteId,
    representada_id: representadaId,
    codigo_cliente_na_representada: codigoNaRepresentada,
    tabela_preco_padrao_id: null,
    observacoes: null,
  })
}

export async function aplicarClientes(
  linhas: LinhaMapeada[],
  representadaId: string,
  padroes: PadroesImportacao,
): Promise<ResultadoClientes> {
  const existentes = await banco.clientes.filter((c) => c.apagado_em === null).toArray()
  const resultado: ResultadoClientes = { criados: 0, atualizados: 0, paraRevisar: [] }

  for (const linha of linhas) {
    const { campos } = linha
    const duplicata = encontrarDuplicata(
      { razao_social: campos.razao_social, cnpj_cpf: campos.cnpj_cpf },
      existentes,
    )
    if (duplicata?.forca === 'forte') {
      const mesclado = mesclarPreenchendoVazios(duplicata.cliente, campos)
      await gravarLocalEEnfileirar('clientes', mesclado)
      await garantirVinculo(mesclado.id, representadaId, campos.codigo_na_representada || null)
      resultado.atualizados++
    } else if (duplicata?.forca === 'provavel') {
      resultado.paraRevisar.push({ linha, existente: duplicata.cliente })
    } else {
      const novo = clienteDaLinha(campos, padroes)
      await gravarLocalEEnfileirar('clientes', novo)
      await garantirVinculo(novo.id, representadaId, campos.codigo_na_representada || null)
      existentes.push(novo) // evita duplicar dentro da mesma planilha
      resultado.criados++
    }
  }
  return resultado
}
